import { useState } from 'react';
import { motion as Motion } from 'framer-motion';
import ProgressRing from './ProgressRing';
import { getRandomBuddyAsset, SLOT } from '../../lib/buddyManifest';
import { SPRING_POP } from '../../constants/motion';
import s from './XPRewardModal.module.css';

const CTA_LINES = [
  "Let's keep going!",
  "You're on fire!",
  "Amazing work!",
  "Keep it up!",
  "Fantastic!",
];

// Full-screen frosted XP overlay (BP-BUDDY-MANIFEST-01, section E). The whole
// overlay IS the dialog — tap anywhere (or the CTA) to dismiss. All entrance
// animation is framer-motion (ADR-044); no CSS keyframes.
export default function XPRewardModal({ progression, onDismiss, buddySlug }) {
  const [ctaLine] = useState(() => CTA_LINES[Math.floor(Math.random() * CTA_LINES.length)]);
  // Random XP_REWARD pose, held for the modal's lifetime.
  const [buddySrc] = useState(() => getRandomBuddyAsset(buddySlug, SLOT.XP_REWARD));

  if (!progression) return null;

  const { xp_awarded, xp_breakdown, level, streak, xp_total } = progression;
  const hasThreshold = typeof level?.next_threshold === 'number';
  const ringThreshold = hasThreshold ? level.next_threshold : xp_total;
  const ringPrev = level?.xp_at_current_level || 0;

  return (
    <Motion.div
      className={s.overlay}
      onClick={onDismiss}
      role="dialog"
      aria-modal="true"
      aria-label="XP Reward"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <div className={s.panel}>
        {/* Buddy — springs down from above */}
        <Motion.div
          className={s.buddyCol}
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...SPRING_POP, delay: 0.3 }}
        >
          {buddySrc && (
            <img
              src={buddySrc}
              alt=""
              className={s.buddyImg}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </Motion.div>

        {/* Right / lower column */}
        <div className={s.infoCol}>
          {/* XP headline */}
          <Motion.div
            className={s.xpHeadline}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.4, ease: 'easeOut' }}
          >
            <span className={s.xpPlus}>+</span>
            <span className={s.xpNumber}>{xp_awarded}</span>
            <span className={s.xpLabel}>XP</span>
          </Motion.div>

          {/* Breakdown rows — staggered fade-in */}
          {(xp_breakdown || []).length > 0 && (
            <div className={s.breakdownWrap}>
              {xp_breakdown.map((item, i) => (
                <Motion.div
                  key={`${item.type}-${i}`}
                  className={s.xpRow}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.5 + i * 0.05, ease: 'easeOut' }}
                >
                  <span className={s.xpRowLabel}>{item.label}</span>
                  <span className={s.xpRowXp}>+{item.xp}</span>
                </Motion.div>
              ))}
            </div>
          )}

          {/* Progress ring + XP label + level name */}
          <Motion.div
            className={s.ringSection}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.55, ease: 'easeOut' }}
          >
            <ProgressRing
              known={xp_total}
              threshold={ringThreshold}
              previousThreshold={ringPrev}
              size={132}
              centerContent={<span className={s.ringLevelNum}>{level?.current ?? 0}</span>}
            />
            <span className={s.xpProgressLabel}>
              {hasThreshold ? `${xp_total} / ${level.next_threshold} XP` : `${xp_total} XP`}
            </span>
            <Motion.span
              className={s.levelNameOrange}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.85 }}
            >
              {level?.name}
            </Motion.span>
          </Motion.div>

          {/* Streak (retained — additive to the spec'd column) */}
          {streak?.updated && streak.current > 1 && (
            <div className={s.streakRow}>
              <span className={s.streakIcon}>🔥</span>
              <span className={s.streakText}>
                {streak.current}-day streak! Come back tomorrow for +{streak.tomorrow_bonus} XP
              </span>
            </div>
          )}

          {/* CTA — fades in last */}
          <Motion.button
            type="button"
            className={s.ctaButton}
            onClick={onDismiss}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 1.0 }}
          >
            {ctaLine}
          </Motion.button>
        </div>
      </div>
    </Motion.div>
  );
}
